import * as React from "react";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import {
  GridRowModes,
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons
} from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import {Alert, AppBar, Card, Typography} from "@mui/material";
import {GET, PATCH, POST} from "APIclient";
import Snackbar from "@mui/material/Snackbar";
import dayjs from "dayjs";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {useLocation} from "react-router-dom";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import properties from "constants/ApplicationConstants";
import {formatDate} from "constants/ApplicationConstants";

export default function AssignProject() {
  const [data, setData] = React.useState([]);
  const location = useLocation();
  const [usersState] = React.useState(location.state);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const fetchUrl = `manager/project-assign/${usersState}?page=0&size=10`;
  const updateUrl = `manager/edit-project-assign`;
  const addUrl = `manager/assignProject`;
  const [open, setOpen] = React.useState(false);
  const [newId, setNewId] = React.useState(1);
  const [snakColor, setSnakColor] = React.useState("");
  const [snakMessage, setSnakMessage] = React.useState("");
  const [clients, setClients] = React.useState([]);
  const [projects, setProjects] = React.useState([]);
  const [projectsForRow, setProjectsForRow] = React.useState({});

  const billableStatuses = [
    {id: 1, value: "Billable"},
    {id: 2, value: "Shadow"},
    {id: 3, value: "Not Billable"}
  ];

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await GET(fetchUrl);
        const fetchedData = response?.data?.data?.paginatedData || [];
        setData(fetchedData);
        if (fetchedData.length > 0) {
          const maxId = Math.max(...fetchedData.map(row => row.assignProjectDetailsId));
          setNewId(maxId + 1);
        } else {
          setNewId(1);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    setTimeout(() => {
      fetchData();
    }, 1000);
  }, [fetchUrl]);

  React.useEffect(() => {
    const fetchClient = async () => {
      try {
        const clientResponse = await GET("admin/client-list?page=0&size=10");
        setClients(clientResponse?.data?.data?.paginatedData || []);
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };
    const fetchProject = async () => {
      try {
        const projectResponse = await GET("admin/project/index");
        setProjects(projectResponse?.data?.data?.paginatedData || []);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchClient();
    fetchProject();
  }, []);

  const handleClientChange = (rowId, selectedClientName) => {
    const selectedClient = clients.find(client => client.clientName === selectedClientName);
    const updatedRows = data.map(row =>
      row.assignProjectDetailsId === rowId
        ? {
            ...row,
            clientId: selectedClient?.clientId || "",
            clientName: selectedClientName || "",
            projectId: null,
            projectName: ""
          }
        : row
    );
    setData(updatedRows);

    const relatedProjects = projects.filter(
      project => project.clientName === selectedClient?.clientName
    );
    setProjectsForRow(prev => ({...prev, [rowId]: relatedProjects}));
  };

  const handleProjectChange = (rowId, selectedProjectId) => {
    const selectedProject = projects.find(project => project.projectId === selectedProjectId);
    const updatedRows = data.map(row =>
      row.assignProjectDetailsId === rowId
        ? {...row, projectId: selectedProjectId, projectName: selectedProject?.projectName || ""}
        : row
    );
    setData(updatedRows);
  };

  const handleBillableStatusChange = (rowId, statusId) => {
    const updatedRows = data.map(row =>
      row.assignProjectDetailsId === rowId ? {...row, billableStatus: statusId} : row
    );
    setData(updatedRows);
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleEditClick = id => () => {
    setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}});
  };

  const handleSaveClick = id => () => {
    setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.View}});
  };

  const handleDeleteClick = id => () => {
    setData(data.filter(row => row.assignProjectDetailsId !== id));
  };

  const handleCancelClick = id => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: {mode: GridRowModes.View, ignoreModifications: true}
    });

    const editedRow = data.find(row => row.assignProjectDetailsId === id);
    if (editedRow.isNew) {
      setData(data.filter(row => row.assignProjectDetailsId !== id));
    }
  };

  const processRowUpdate = async newRow => {
    const isNewRow = newRow.isNew;
    const updatedRow = {...newRow, isNew: false};

    const formattedAssignFrom = newRow.assignFrom
      ? dayjs(newRow.assignFrom).format("YYYY-MM-DD")
      : null;
    const formattedEndDate = newRow.endDate ? dayjs(newRow.endDate).format("YYYY-MM-DD") : null;

    const payload = {
      assignProjectDetailsId: updatedRow.assignProjectDetailsId,
      empId: String(usersState),
      projectId: parseInt(updatedRow.projectId) || null,
      clientId: parseInt(updatedRow.clientId) || null,
      assignFrom: formattedAssignFrom,
      endDate: formattedEndDate,
      billableStatus: parseInt(updatedRow.billableStatus) || null
    };

    try {
      const response = await (isNewRow ? POST(addUrl, payload) : PATCH(updateUrl, payload));

      if (response?.data?.statusCode === 200) {
        setSnakColor("success");
        setSnakMessage("Project assigned successfully");
        setOpen(true);

        const responseDt = await GET(fetchUrl);
        setData(responseDt?.data?.data?.paginatedData || []);
      }
    } catch (error) {
      const err = error?.response;
      const errorMessage =
        err?.data?.statusCode === 409 ||
        err?.data?.statusCode === 406 ||
        err?.data?.statusCode === 403
          ? err.data.message
          : error.message || "An error occurred";
      setSnakColor("error");
      setSnakMessage(errorMessage);
      setOpen(true);
      throw error;
    }

    return updatedRow;
  };

  const handleProcessRowUpdateError = error => {
    setSnakColor("error");
    setSnakMessage(error.message || "Error updating row");
    setOpen(true);
  };

  const handleRowModesModelChange = newRowModesModel => {
    setRowModesModel(newRowModesModel);
  };

  const columns = [
    {field: "assignProjectDetailsId", headerName: "ID", hide: true},
    {
      field: "clientName",
      headerName: "Client Name",
      width: 140,
      editable: true,
      renderEditCell: params => (
        <Select
          fullWidth
          IconComponent={ExpandMoreIcon}
          value={params.value || ""}
          onChange={event => {
            handleClientChange(params.id, event.target.value);
            params.api.setEditCellValue({
              id: params.id,
              field: "clientName",
              value: event.target.value
            });
          }}
        >
          <MenuItem value="">
            <em>Select</em>
          </MenuItem>
          {clients.map((client, index) => (
            <MenuItem key={index} value={client.clientName}>
              {client.clientName}
            </MenuItem>
          ))}
        </Select>
      ),
      valueGetter: params => {
        console.log("The value of project: ", params);
        const selectedClientName = projects.find(project => project.clientName === params);
        return selectedClientName ? selectedClientName.clientName : params.row?.clientName || "N/A";
      }
    },
    {
      field: "projectName",
      headerName: "Project Name",
      width: 230,
      editable: true,
      renderEditCell: params => {
        const filteredProjects = projectsForRow[params.row?.assignProjectDetailsId] || [];
        return (
          <Select
            fullWidth
            value={params.row.projectId || ""}
            onChange={event => handleProjectChange(params.id, event.target.value)}
          >
            <MenuItem value="">
              <em>Select</em>
            </MenuItem>
            {filteredProjects.map(project => (
              <MenuItem key={project.projectId} value={project.projectId}>
                {project.projectName}
              </MenuItem>
            ))}
          </Select>
        );
      },
      valueGetter: params => {
        console.log("The value of project: ", params);
        const selectedProject = projects.find(project => project.projectName === params);
        return selectedProject ? selectedProject.projectName : params.row?.projectName || "N/A";
      }
    },
    {
      field: "assignFrom",
      headerName: "Start Date",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: params => formatDate(params.value),
      renderEditCell: params => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            format="DD/MM/YYYY"
            value={params.value ? dayjs(params.value) : null}
            onChange={date =>
              params.api.setEditCellValue({id: params.id, field: params.field, value: date})
            }
          />
        </LocalizationProvider>
      ),
      editable: true
    },
    {
      field: "endDate",
      headerName: "End Date",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: params => formatDate(params.value),
      renderEditCell: params => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            format="DD/MM/YYYY"
            value={params.value ? dayjs(params.value) : null}
            onChange={date =>
              params.api.setEditCellValue({id: params.id, field: params.field, value: date})
            }
          />
        </LocalizationProvider>
      ),
      editable: true
    },
    {
      field: "billableStatus",
      headerName: "Billing Status",
      width: 150,
      align: "left",
      headerAlign: "left",
      editable: true,
      type: "singleSelect",
      renderEditCell: params => (
        <Select
          fullWidth
          value={params.row.billableStatus || ""}
          IconComponent={ExpandMoreIcon}
          onChange={event => {
            handleBillableStatusChange(params.id, event.target.value);
            params.api.setEditCellValue({
              id: params.id,
              field: "billableStatus",
              value: event.target.value
            });
          }}
        >
          <MenuItem value="">
            <em>Select</em>
          </MenuItem>
          {billableStatuses.map(status => (
            <MenuItem key={status.id} value={status.id}>
              {status.value}
            </MenuItem>
          ))}
        </Select>
      ),
      valueGetter: params => {
        const selectedStatus = billableStatuses.find(status => status.id === params);
        return selectedStatus ? selectedStatus.value : "N/A";
      }
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 90,
      cellClassName: "actions",
      getActions: ({id}) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{color: "primary.main"}}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />
          // <GridActionsCellItem
          //   icon={<DeleteIcon />}
          //   label="Delete"
          //   onClick={handleDeleteClick(id)}
          //   color="inherit"
          // />
        ];
      }
    }
  ];

  const handleNullValues = data => {
    return data && data.length
      ? data.map(row => ({
          ...row,
          clientName: row.clientName ?? "N/A",
          projectName:
            projects.find(project => project.projectId === row.projectId)?.projectName ??
            row.projectName ??
            "N/A",
          assignFrom: row.assignFrom ?? "Unknown",
          endDate: row.endDate ?? "Unknown",
          billableStatus: row.billableStatus ?? "N/A",
          clientId: row.clientId ?? "N/A",
          projectId: row.projectId ?? "N/A"
        }))
      : [];
  };

  const cleanedRows = handleNullValues(data);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <Typography
              sx={{
                color: "textPrimary",
                fontFamily: properties.fontFamily,
                fontSize: "16px",
                px: 2,
                pt: 2,
                fontWeight: "bold"
              }}
            >
              Assigned Project Details For {usersState}
            </Typography>

            <MDBox px={2} pt={2} pb={3}>
              <MDBox
                sx={{
                  height: 500,
                  width: "100%",
                  "& .actions": {color: "text.secondary"},
                  "& .textPrimary": {color: "text.primary"}
                }}
              >
                <DataGrid
                  rows={cleanedRows}
                  columns={columns}
                  editMode="row"
                  rowModesModel={rowModesModel}
                  getRowId={row => row?.assignProjectDetailsId}
                  columnVisibilityModel={{assignProjectDetailsId: false}}
                  sx={{fontFamily: properties.fontFamily, fontSize: "14px"}}
                  onRowModesModelChange={handleRowModesModelChange}
                  onRowEditStop={handleRowEditStop}
                  processRowUpdate={processRowUpdate}
                  onProcessRowUpdateError={handleProcessRowUpdateError}
                  slots={{toolbar: EditToolbar}}
                  slotProps={{toolbar: {data, setRowModesModel}}}
                  initialState={{
                    sorting: {sortModel: [{field: "assignProjectDetailsId", sort: "asc"}]}
                  }}
                />
                <Snackbar
                  open={open}
                  anchorOrigin={{vertical: "top", horizontal: "right"}}
                  autoHideDuration={1200}
                  onClose={handleClose}
                >
                  <Alert
                    onClose={handleClose}
                    severity={snakColor === "success" ? "success" : "error"}
                    variant="filled"
                    sx={{width: "100%"}}
                  >
                    {snakMessage}
                  </Alert>
                </Snackbar>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>
      </MDBox>
    </DashboardLayout>
  );

  function EditToolbar(props) {
    const {setRowModesModel} = props;

    const handleClick = () => {
      const newRow = {
        assignProjectDetailsId: newId,
        clientId: "",
        projectId: "",
        clientName: "",
        projectName: "",
        assignFrom: "",
        endDate: "",
        billableStatus: "",
        isNew: true
      };
      setData(prevRows => [...(prevRows || []), newRow]);
      setRowModesModel(oldModel => ({
        ...oldModel,
        [newId]: {mode: GridRowModes.Edit, fieldToFocus: "clientName"}
      }));
      setNewId(prevId => prevId + 1);
    };

    return (
      <GridToolbarContainer>
        <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
          Add project
        </Button>
      </GridToolbarContainer>
    );
  }
}
